import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  Chip,
  Card,
  CardContent,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchMatchById, updateMatchScore } from '../../services/api';
import { format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { useUserLookup } from '../../hooks/useUserLookup';

const MatchDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [set1Player1, setSet1Player1] = useState('');
  const [set1Player2, setSet1Player2] = useState('');
  const [set1Player1Tiebreak, setSet1Player1Tiebreak] = useState('');
  const [set1Player2Tiebreak, setSet1Player2Tiebreak] = useState('');
  
  const [set2Player1, setSet2Player1] = useState('');
  const [set2Player2, setSet2Player2] = useState('');
  const [set2Player1Tiebreak, setSet2Player1Tiebreak] = useState('');
  const [set2Player2Tiebreak, setSet2Player2Tiebreak] = useState('');
  
  const [set3Player1, setSet3Player1] = useState('');
  const [set3Player2, setSet3Player2] = useState('');
  const [set3Player1Tiebreak, setSet3Player1Tiebreak] = useState('');
  const [set3Player2Tiebreak, setSet3Player2Tiebreak] = useState('');
  
  const [errorMessage, setErrorMessage] = useState('');
  const [set1Error, setSet1Error] = useState('');
  const [set2Error, setSet2Error] = useState('');
  const [set3Error, setSet3Error] = useState('');

  const { data: match, isLoading: matchLoading } = useQuery(
    ['match', id],
    () => fetchMatchById(id!),
    { enabled: !!id }
  );

  const updateMatchMutation = useMutation(
    (score: any) => updateMatchScore(id!, score),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['match', id]);
        queryClient.invalidateQueries(['matches']);
      },
    }
  );

  // Define validateSetScore before useEffect hooks
  const validateSetScore = (score1: number, score2: number): string | null => {
    // Both scores must be between 0 and 7
    if (score1 < 0 || score1 > 7 || score2 < 0 || score2 > 7) {
      return 'Scores must be between 0 and 7';
    }

    // Check for valid tennis scores
    const higher = Math.max(score1, score2);
    const lower = Math.min(score1, score2);

    // Normal set: winner must have 6+ games and be ahead by 2
    if (higher === 6 && lower <= 4) {
      return null; // Valid: 6-0, 6-1, 6-2, 6-3, 6-4
    }

    // Tiebreak set: 7-5 or 7-6
    if (higher === 7 && (lower === 5 || lower === 6)) {
      return null; // Valid
    }

    return `Invalid tennis score: ${score1}-${score2}. Winner must win by 2 games (6-4 or less) or win tiebreak (7-5, 7-6)`;
  };

  // Real-time validation for Set 1
  useEffect(() => {
    if (set1Player1 && set1Player2) {
      const s1 = parseInt(set1Player1);
      const s2 = parseInt(set1Player2);
      const error = validateSetScore(s1, s2);
      setSet1Error(error || '');
    } else {
      setSet1Error('');
    }
  }, [set1Player1, set1Player2]);

  // Real-time validation for Set 2
  useEffect(() => {
    if (set2Player1 && set2Player2) {
      const s1 = parseInt(set2Player1);
      const s2 = parseInt(set2Player2);
      const error = validateSetScore(s1, s2);
      setSet2Error(error || '');
    } else {
      setSet2Error('');
    }
  }, [set2Player1, set2Player2]);

  // Real-time validation for Set 3
  useEffect(() => {
    if (set3Player1 && set3Player2) {
      const s1 = parseInt(set3Player1);
      const s2 = parseInt(set3Player2);
      const error = validateSetScore(s1, s2);
      setSet3Error(error || '');
    } else {
      setSet3Error('');
    }
  }, [set3Player1, set3Player2]);

  // Populate score fields when match loads (especially for completed matches that admins want to update)
  useEffect(() => {
    if (match && match.score) {
      const score = match.score;
      
      // Backend format: { sets: [{ set_number: 1, player1_games: 6, player2_games: 4, player1_tiebreak: 0, player2_tiebreak: 0 }, ...] }
      if (score.sets && Array.isArray(score.sets)) {
        score.sets.forEach((set: any) => {
          const setNum = set.set_number || 0;
          const player1Games = set.player1_games ?? set.player1 ?? 0;
          const player2Games = set.player2_games ?? set.player2 ?? 0;
          const player1Tiebreak = set.player1_tiebreak ?? set.tiebreak1 ?? 0;
          const player2Tiebreak = set.player2_tiebreak ?? set.tiebreak2 ?? 0;
          
          if (setNum === 1 || setNum === 0) {
            setSet1Player1(player1Games > 0 ? player1Games.toString() : '');
            setSet1Player2(player2Games > 0 ? player2Games.toString() : '');
            setSet1Player1Tiebreak(player1Tiebreak > 0 ? player1Tiebreak.toString() : '');
            setSet1Player2Tiebreak(player2Tiebreak > 0 ? player2Tiebreak.toString() : '');
          } else if (setNum === 2) {
            setSet2Player1(player1Games > 0 ? player1Games.toString() : '');
            setSet2Player2(player2Games > 0 ? player2Games.toString() : '');
            setSet2Player1Tiebreak(player1Tiebreak > 0 ? player1Tiebreak.toString() : '');
            setSet2Player2Tiebreak(player2Tiebreak > 0 ? player2Tiebreak.toString() : '');
          } else if (setNum === 3) {
            setSet3Player1(player1Games > 0 ? player1Games.toString() : '');
            setSet3Player2(player2Games > 0 ? player2Games.toString() : '');
            setSet3Player1Tiebreak(player1Tiebreak > 0 ? player1Tiebreak.toString() : '');
            setSet3Player2Tiebreak(player2Tiebreak > 0 ? player2Tiebreak.toString() : '');
          }
        });
      }
    } else {
      // Clear fields if no score
      setSet1Player1('');
      setSet1Player2('');
      setSet1Player1Tiebreak('');
      setSet1Player2Tiebreak('');
      setSet2Player1('');
      setSet2Player2('');
      setSet2Player1Tiebreak('');
      setSet2Player2Tiebreak('');
      setSet3Player1('');
      setSet3Player2('');
      setSet3Player1Tiebreak('');
      setSet3Player2Tiebreak('');
    }
  }, [match]);

  // Use user lookup hook for fallback (in case backend doesn't populate player info)
  const playerIds = match ? [match.player1_id, match.player2_id].filter(Boolean) as string[] : [];
  const { getUserName: getUserNameFromHook } = useUserLookup(playerIds);

  // Early returns after all hooks
  if (matchLoading) return <div>Loading...</div>;
  if (!match) return <div>Match not found</div>;

  // Prefer backend-populated player info, fall back to hook lookup
  const getUserName = (userId?: string) => {
    if (!userId) return 'TBD';
    
    // First, try to use backend-populated player info (most reliable)
    if (match.player1_id === userId && match.player1) {
      return match.player1.name;
    }
    if (match.player2_id === userId && match.player2) {
      return match.player2.name;
    }
    
    // Fallback to hook lookup (handles ID mismatches)
    return getUserNameFromHook(userId);
  };

  const isPlayer = user && (match.player1_id === user.userId || match.player2_id === user.userId);
  const userRole = user?.['custom:role'];
  const isAdmin = userRole === 'admin';
  // Players can enter scores for scheduled matches, admins can enter/update scores for any match
  const canEnterScore = (isPlayer && match.status !== 'completed') || isAdmin;

  const handleSubmitScore = () => {
    setErrorMessage('');
    const sets = [];
    
    // Validate and collect Set 1
    if (set1Player1 && set1Player2) {
      const s1 = parseInt(set1Player1);
      const s2 = parseInt(set1Player2);
      const error = validateSetScore(s1, s2);
      if (error) {
        setErrorMessage(`Set 1: ${error}`);
        return;
      }
      const setData: any = { 
        set_number: 1,
        player1_games: s1, 
        player2_games: s2 
      };
      if (set1Player1Tiebreak) setData.player1_tiebreak = parseInt(set1Player1Tiebreak);
      if (set1Player2Tiebreak) setData.player2_tiebreak = parseInt(set1Player2Tiebreak);
      sets.push(setData);
    }
    
    // Validate and collect Set 2
    if (set2Player1 && set2Player2) {
      const s1 = parseInt(set2Player1);
      const s2 = parseInt(set2Player2);
      const error = validateSetScore(s1, s2);
      if (error) {
        setErrorMessage(`Set 2: ${error}`);
        return;
      }
      const setData: any = { 
        set_number: 2,
        player1_games: s1, 
        player2_games: s2 
      };
      if (set2Player1Tiebreak) setData.player1_tiebreak = parseInt(set2Player1Tiebreak);
      if (set2Player2Tiebreak) setData.player2_tiebreak = parseInt(set2Player2Tiebreak);
      sets.push(setData);
    }
    
    // Validate and collect Set 3 (if provided)
    if (set3Player1 && set3Player2) {
      const s1 = parseInt(set3Player1);
      const s2 = parseInt(set3Player2);
      const error = validateSetScore(s1, s2);
      if (error) {
        setErrorMessage(`Set 3: ${error}`);
        return;
      }
      const setData: any = { 
        set_number: 3,
        player1_games: s1, 
        player2_games: s2 
      };
      if (set3Player1Tiebreak) setData.player1_tiebreak = parseInt(set3Player1Tiebreak);
      if (set3Player2Tiebreak) setData.player2_tiebreak = parseInt(set3Player2Tiebreak);
      sets.push(setData);
    }

    // Must have at least 2 sets
    if (sets.length < 2) {
      setErrorMessage('You must enter scores for at least Set 1 and Set 2');
      return;
    }

    // Determine winner by counting sets won
    let player1Sets = 0;
    let player2Sets = 0;
    
    sets.forEach((set) => {
      if (set.player1_games > set.player2_games) player1Sets++;
      else if (set.player2_games > set.player1_games) player2Sets++;
    });

    // Check for valid match result (one player must win 2 sets)
    if (player1Sets < 2 && player2Sets < 2) {
      setErrorMessage('Invalid match result: One player must win at least 2 sets. Please enter Set 3 scores.');
      return;
    }

    // UpdateScore handler automatically calculates winner and sets status to completed
    updateMatchMutation.mutate({ sets });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'warning';
      case 'scheduled':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Button onClick={() => navigate(-1)} sx={{ mb: 2 }}>
        ← Back
      </Button>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4">Match Details</Typography>
          <Chip
            label={match.status.replace('_', ' ')}
            color={getStatusColor(match.status)}
            sx={{ textTransform: 'capitalize' }}
          />
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Match Information
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Tournament: {match.tournament_name || match.tournament_id}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Round: {match.round}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Match Number: {match.match_number}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Type: {match.match_type}
                </Typography>
                {match.scheduled_time && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Scheduled: {format(new Date(match.scheduled_time), 'MMM dd, yyyy h:mm a')}
                  </Typography>
                )}
                {match.court && (
                  <Typography variant="body2" color="text.secondary">
                    Court: {match.court}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {match.match_type === 'doubles' ? 'Teams' : 'Players'}
                </Typography>
                {match.match_type === 'singles' ? (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: match.winner_id === match.player1_id || match.winner_player_id === match.player1_id ? 'bold' : 'normal',
                          color: match.winner_id === match.player1_id || match.winner_player_id === match.player1_id ? 'success.main' : 'inherit',
                        }}
                      >
                        {getUserName(match.player1_id)}
                        {(match.winner_id === match.player1_id || match.winner_player_id === match.player1_id) && ' (Winner)'}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      vs
                    </Typography>
                    <Box>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: match.winner_id === match.player2_id || match.winner_player_id === match.player2_id ? 'bold' : 'normal',
                          color: match.winner_id === match.player2_id || match.winner_player_id === match.player2_id ? 'success.main' : 'inherit',
                        }}
                      >
                        {getUserName(match.player2_id)}
                        {(match.winner_id === match.player2_id || match.winner_player_id === match.player2_id) && ' (Winner)'}
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: match.winner_team_id === match.team1_id ? 'bold' : 'normal',
                          color: match.winner_team_id === match.team1_id ? 'success.main' : 'inherit',
                        }}
                      >
                        {match.team1?.team_name || 'Team 1'}
                        {match.winner_team_id === match.team1_id && ' (Winner)'}
                      </Typography>
                      {match.team1?.players && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {match.team1.players.map((p: any) => p.name).join(' & ')}
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      vs
                    </Typography>
                    <Box>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: match.winner_team_id === match.team2_id ? 'bold' : 'normal',
                          color: match.winner_team_id === match.team2_id ? 'success.main' : 'inherit',
                        }}
                      >
                        {match.team2?.team_name || 'Team 2'}
                        {match.winner_team_id === match.team2_id && ' (Winner)'}
                      </Typography>
                      {match.team2?.players && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {match.team2.players.map((p: any) => p.name).join(' & ')}
                        </Typography>
                      )}
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {match.status === 'completed' && match.score && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Score
          </Typography>
          <Grid container spacing={2}>
            {match.score.sets?.map((set: any, index: number) => (
              <Grid item xs={12} sm={4} key={index}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      Set {index + 1}
                    </Typography>
                    <Typography variant="h5">
                      {set.player1_games} - {set.player2_games}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {canEnterScore && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {match.status === 'completed' ? 'Update Score' : 'Enter Score'}
          </Typography>

          {isAdmin && (
            <Alert severity="info" sx={{ mb: 3 }}>
              {match.status === 'completed' 
                ? 'You are updating the score as an administrator. This will overwrite the existing score.'
                : 'You are entering the score as an administrator'}
            </Alert>
          )}

          {errorMessage && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setErrorMessage('')}>
              {errorMessage}
            </Alert>
          )}

          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, width: 150 }}>Player</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Set 1</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, width: 60 }}>TB</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Set 2</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, width: 60 }}>TB</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Set 3</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, width: 60 }}>TB</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Player 1 Row */}
                <TableRow>
                  <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 500 }}>
                    {getUserName(match.player1_id)}
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      type="number"
                      value={set1Player1}
                      onChange={(e) => setSet1Player1(e.target.value)}
                      size="small"
                      error={!!set1Error}
                      inputProps={{ min: 0, max: 7, style: { textAlign: 'center', width: 40 } }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {(set1Player1 === '7' || set1Player2 === '7') && (
                      <TextField
                        type="number"
                        value={set1Player1Tiebreak}
                        onChange={(e) => setSet1Player1Tiebreak(e.target.value)}
                        size="small"
                        inputProps={{ min: 0, style: { textAlign: 'center', width: 40 } }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      type="number"
                      value={set2Player1}
                      onChange={(e) => setSet2Player1(e.target.value)}
                      size="small"
                      error={!!set2Error}
                      inputProps={{ min: 0, max: 7, style: { textAlign: 'center', width: 40 } }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {(set2Player1 === '7' || set2Player2 === '7') && (
                      <TextField
                        type="number"
                        value={set2Player1Tiebreak}
                        onChange={(e) => setSet2Player1Tiebreak(e.target.value)}
                        size="small"
                        inputProps={{ min: 0, style: { textAlign: 'center', width: 40 } }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      type="number"
                      value={set3Player1}
                      onChange={(e) => setSet3Player1(e.target.value)}
                      size="small"
                      error={!!set3Error}
                      inputProps={{ min: 0, max: 7, style: { textAlign: 'center', width: 40 } }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {(set3Player1 === '7' || set3Player2 === '7') && (
                      <TextField
                        type="number"
                        value={set3Player1Tiebreak}
                        onChange={(e) => setSet3Player1Tiebreak(e.target.value)}
                        size="small"
                        inputProps={{ min: 0, style: { textAlign: 'center', width: 40 } }}
                      />
                    )}
                  </TableCell>
                </TableRow>

                {/* Player 2 Row */}
                <TableRow>
                  <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 500 }}>
                    {getUserName(match.player2_id)}
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      type="number"
                      value={set1Player2}
                      onChange={(e) => setSet1Player2(e.target.value)}
                      size="small"
                      error={!!set1Error}
                      inputProps={{ min: 0, max: 7, style: { textAlign: 'center', width: 40 } }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {(set1Player1 === '7' || set1Player2 === '7') && (
                      <TextField
                        type="number"
                        value={set1Player2Tiebreak}
                        onChange={(e) => setSet1Player2Tiebreak(e.target.value)}
                        size="small"
                        inputProps={{ min: 0, style: { textAlign: 'center', width: 40 } }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      type="number"
                      value={set2Player2}
                      onChange={(e) => setSet2Player2(e.target.value)}
                      size="small"
                      error={!!set2Error}
                      inputProps={{ min: 0, max: 7, style: { textAlign: 'center', width: 40 } }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {(set2Player1 === '7' || set2Player2 === '7') && (
                      <TextField
                        type="number"
                        value={set2Player2Tiebreak}
                        onChange={(e) => setSet2Player2Tiebreak(e.target.value)}
                        size="small"
                        inputProps={{ min: 0, style: { textAlign: 'center', width: 40 } }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      type="number"
                      value={set3Player2}
                      onChange={(e) => setSet3Player2(e.target.value)}
                      size="small"
                      error={!!set3Error}
                      inputProps={{ min: 0, max: 7, style: { textAlign: 'center', width: 40 } }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {(set3Player1 === '7' || set3Player2 === '7') && (
                      <TextField
                        type="number"
                        value={set3Player2Tiebreak}
                        onChange={(e) => setSet3Player2Tiebreak(e.target.value)}
                        size="small"
                        inputProps={{ min: 0, style: { textAlign: 'center', width: 40 } }}
                      />
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {(set1Error || set2Error || set3Error) && (
            <Box sx={{ mb: 2 }}>
              {set1Error && <Typography variant="caption" color="error" display="block">Set 1: {set1Error}</Typography>}
              {set2Error && <Typography variant="caption" color="error" display="block">Set 2: {set2Error}</Typography>}
              {set3Error && <Typography variant="caption" color="error" display="block">Set 3: {set3Error}</Typography>}
            </Box>
          )}

          <Button
            variant="contained"
            onClick={handleSubmitScore}
            disabled={!set1Player1 || !set1Player2 || !set2Player1 || !set2Player2 || !!set1Error || !!set2Error || !!set3Error}
            size="large"
          >
            Submit Score
          </Button>
        </Paper>
      )}
    </Box>
  );
};

export default MatchDetailPage;
